import { useState } from 'react';
import {
  Box,
  Container,
  Flex,
  HStack,
  Text,
  Menu,
  SimpleGrid,
  VStack,
  Icon,
  Card,
  Spacer,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Use the hook for functional components

// Import your own components
import { LogoWordmark } from '../icons/Logo';
import { routeTo } from '@/router/routeTo';

// Import icons for the new menu
import { FiBarChart2, FiMessageSquare, FiAlertTriangle, FiFileText, FiGrid, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { useAuth } from '@/hooks/useAuth';

// --- Improved FeatureMenuItem ---
type FeatureMenuItemProps = {
  icon: React.ElementType;
  title: string;
  to: string;
  description: string;
};

const FeatureMenuItem: React.FC<FeatureMenuItemProps> = ({ icon, title, to, description }) => {
  return (
    // The NavLink now acts as a block-level link wrapping the entire card
    <NavLink to={to} style={{ textDecoration: 'none' }}>
      {/* By default, Card is not a interactive element. In order for it to be a child of a link
        we need to add `isFocusable` prop to it
      */}
      <Card.Root
        // isFocusable
        // --- Fixed Size ---
        w="220px"
        h="120px"
        // --- Improved Hover Effect ---
        transition="all .2s ease-in-out"
        _hover={{
          transform: 'scale(1.03)',
          bg: 'whiteAlpha.100', // A subtle background highlight on hover
        }}
      >
        <Card.Body>
          {/* --- Improved Internal Layout --- */}
          <VStack align="start" justify="center" h="100%" gap={1}>
            <Icon as={icon} boxSize={6} color="blue.300" />
            <Text fontWeight="semibold" fontSize="md" color="white">
              {title}
            </Text>
            {/* Corrected the description display */}
            <Text fontSize="sm" color="gray.400">
              {description}
            </Text>
          </VStack>
        </Card.Body>
      </Card.Root>
    </NavLink>
  );
};

// --- Updated FeaturesMenu with Translations ---
const FeaturesMenu = () => {
  const { t } = useTranslation(); // Use the translation hook
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Menu.Root open={isMenuOpen} onOpenChange={(details) => setIsMenuOpen(details.open)}>
      <Box onMouseEnter={() => setIsMenuOpen(true)} onMouseLeave={() => setIsMenuOpen(false)}>
        {/* Use a button as a trigger for better accessibility */}
        <Menu.Trigger asChild>
          <HStack
            as="button"
            bg="transparent"
            _hover={{ bg: 'whiteAlpha.200' }}
            px={3}
            py={2}
            borderRadius="md"
            color="white"
            cursor="pointer"
          >
            <Text>{t('public:header.features')}</Text>
            {/* Changed icons to chevrons for a cleaner look */}
            <Icon as={isMenuOpen ? FiChevronUp : FiChevronDown} />
          </HStack>
        </Menu.Trigger>

        <Menu.Positioner>
          <Menu.Content
            bg={"body-bg"}
            border="1px solid"
            borderColor={"border"}
            p={4}
            borderRadius="xl"
            boxShadow="lg"
            // The width will be determined by the grid content now
            w="auto"
          >
            {/* The gap is now on the grid for consistent spacing */}
            <SimpleGrid columns={3} gap={4}>
              <FeatureMenuItem
                icon={FiBarChart2}
                title={t('public:featuresMenu.sentiment.title')}
                description={t('public:featuresMenu.sentiment.description')}
                to={routeTo.featuresDashboard()} // Note: Matched route to title
              />
              <FeatureMenuItem
                icon={FiMessageSquare}
                title={t('public:featuresMenu.aiAssistant.title')}
                description={t('public:featuresMenu.aiAssistant.description')}
                to={routeTo.featuresAIAssistant()}
              />
              <FeatureMenuItem
                icon={FiAlertTriangle}
                title={t('public:featuresMenu.crisisDetection.title')}
                description={t('public:featuresMenu.crisisDetection.description')}
                to={routeTo.featuresCrisisDetection()}
              />
              <FeatureMenuItem
                icon={FiFileText}
                title={t('public:featuresMenu.reporting.title')}
                description={t('public:featuresMenu.reporting.description')}
                to={routeTo.featuresReporting()}
              />
              <FeatureMenuItem
                icon={FiGrid}
                title={t('public:featuresMenu.crossPlatform.title')}
                description={t('public:featuresMenu.crossPlatform.description')}
                to={routeTo.featuresCrossPlatform()} // Note: Matched route to title
              />
            </SimpleGrid>
          </Menu.Content>
        </Menu.Positioner>
      </Box>
    </Menu.Root>
  );
};

// --- Updated PublicHeader with Translations ---
export const PublicHeader = () => {
  const { t } = useTranslation(['public']); // Use the translation hook
  const { isAuthenticated } = useAuth();

  return (
    <Box
      as="header"
      position="fixed"
      top={3}
      left={4}
      right={4}
      maxW={{ base: '98%', lg: '80%' }} // Slightly wider for large screens
      mx="auto"
      // bg="blackAlpha.600" // Added a semi-transparent background for readability
      bg="bg.header"
      backdropFilter="saturate(180%) blur(5px)"
      boxShadow="md"
      zIndex="sticky"
      borderRadius="xl"
      border="1px solid"
      borderColor="whiteAlpha.300" // A subtle border for definition
      height="60px"
    >
      <Container maxW="container.xl" px={4} py={2}>
        <Flex align="center" justify="space-between">
          <Box height="40px">
            <LogoWordmark />
          </Box>
          {/* <LogoMark /> */}

          <Spacer />
          <HStack>
            <HStack gap={1}> {/* Reduced gap to manage spacing via padding */}
              <FeaturesMenu />
              <NavLink to={routeTo.extension()} style={{ textDecoration: 'none' }}>
                <Text _hover={{ bg: 'whiteAlpha.200' }} px={3} py={2} borderRadius="md" color="white">
                  {t('public:header.browserExtension')}
                </Text>
              </NavLink>
              <NavLink to={routeTo.pricing()} style={{ textDecoration: 'none' }}>
                <Text _hover={{ bg: 'whiteAlpha.200' }} px={3} py={2} borderRadius="md" color="white">
                  {t('public:header.pricing')}
                </Text>
              </NavLink>
            </HStack>

            {isAuthenticated ? (
              <NavLink to={routeTo.dashboard()}>
                <Text _hover={{ bg: 'whiteAlpha.200' }} px={3} py={2} borderRadius="md" color="white">
                  {t('public:header.dashboard')}
                </Text>
              </NavLink>
            ) : (
              <HStack gap={4}>
                <NavLink to={routeTo.signIn()}>
                  <Text color="white" _hover={{ textDecoration: 'underline' }}>
                    {t('public:header.signIn')}
                  </Text>
                </NavLink>
                <NavLink to={routeTo.signUp()}>
                  <Text color="white" _hover={{ textDecoration: 'underline' }}>
                    {t('public:header.signUp')}
                  </Text>
                </NavLink>
              </HStack>
            )}
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};